import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'

import { ListItem, ListItemText, Collapse, List, IconButton, Typography, useMediaQuery, Box } from '@mui/material'

import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandMore from '@mui/icons-material/ExpandMore'

import { get } from 'utils/lodash'
import { isCustomerDetailsPage } from 'router/routes'
import { combineAddressInfo } from 'utils/helper'
import { CACHE_TAG_ACCOUNT_DETAILS_BY_ID } from 'api/cacheTagTypes'
import { selectAddressName } from 'data/address/addressSelectors'

import api from 'api'
import LocationAvatar from 'components/common/avatar/LocationAvatar'
import { useDispatch } from 'react-redux'
import { getCustomerDetails } from 'middleware/actions/customers'
import LocationExpandView from 'components/customer-details/content/location/LocationExpandView'

const LocationView = ({
  location = {},
  showServiceCount = false,
  accountId,
  action,
  onRowClick = noop,
  showLocationAvatar = true,
  sx,
  showContacts = true,
}) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const locationId = get(location, 'id')
  const locationAddress = get(location, 'address', {})
  const locationAddressFormatted = combineAddressInfo(locationAddress)
  const addressName = selectAddressName(locationAddress)
  const serviceCount = get(location, 'serviceCount', 0)
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('xl'))
  const orientation = isMobile ? 'vertical' : 'horizontal'
  const isTabletOrMobile = useMediaQuery(theme => theme.breakpoints.down('lg'))
  const dynamicWidth = isTabletOrMobile && orientation === 'horizontal'
  const handleClick = () => setOpen(!open)

  useEffect(() => {
    if (locationId) {
      setOpen(false)
    }
  }, [locationId])

  const onSaveTags = () => {
    if (isCustomerDetailsPage()) {
      dispatch(getCustomerDetails({ accountId }))
    }
    dispatch(api.util.invalidateTags([{ type: CACHE_TAG_ACCOUNT_DETAILS_BY_ID, id: accountId }]))
  }

  return (
    <>
      <ListItem sx={sx} onClick={onRowClick}>
        <IconButton
          size="small"
          onClick={event => {
            event.stopPropagation()
            handleClick()
          }}
        >
          {open ? <ExpandMore /> : <ChevronRight />}
        </IconButton>

        <ListItemText
          sx={{ display: 'grid' }}
          primary={
            <Box display="flex" alignItems="center" gap={0.5}>
              {addressName && (
                <Typography variant="h5" fontWeight={500} noWrap>
                  {addressName}
                  {showServiceCount && ' | '}
                </Typography>
              )}
              {showServiceCount && (
                <Typography variant="h6" fontWeight={400}>
                  {`${serviceCount} active service${serviceCount === 1 ? '' : 's'}`}
                </Typography>
              )}
            </Box>
          }
          secondary={locationAddressFormatted}
          slotProps={{
            primary: { color: 'textSecondary', noWrap: true },
            secondary: { variant: 'h5', fontWeight: 400, color: 'textPrimary', noWrap: true },
          }}
        />

        {showLocationAvatar && <LocationAvatar isActive={false} />}
        {action}
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItem disablePadding sx={{ pt: 0, pl: 6, pr: 2.5, pb: 1 }}>
            <LocationExpandView
              caller="list"
              accountId={accountId}
              selectedLocation={location}
              orientation={orientation}
              dynamicWidth={dynamicWidth}
              onSaveTags={onSaveTags}
              showContacts={showContacts}
              showParcelNumber
            />
          </ListItem>
        </List>
      </Collapse>
    </>
  )
}

LocationView.propTypes = {
  location: PropTypes.object,
  showServiceCount: PropTypes.bool,
  accountId: PropTypes.string.isRequired,
  action: PropTypes.node,
  onRowClick: PropTypes.func,
  showLocationAvatar: PropTypes.bool,
  sx: PropTypes.object,
  showContacts: PropTypes.bool,
}

export default LocationView
